import {
  faCancel,
  faPencil,
  faSave,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Structure } from "../../types/Structure";
import { Structures } from "../../api/Structure";
import { useToasts } from "../../contexts/toasts";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { TailSpin } from "react-loader-spinner";
import { Button, Form, Input, Select, Upload, UploadFile } from "antd";
import GooglePlacesSelect from "./GooglePlacesSelect";
import { UploadProps } from "antd/lib";
import { getMimeType } from "../../utils/stringUtil";
import classNames from "classnames";
import { BookingMode } from "../../enums/BookingScope";
import { User } from "../../api/User";
import { useAuth } from "../../contexts/auth";

const formLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    md: {
      span: 24,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    md: {
      span: 16,
    },
  },
};

export default function StructureData() {
  const { showToast } = useToasts();
  const navigate = useNavigate();
  const { token, setLoggedUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [editLoading, setEditLoading] = useState(false);
  const [structure, setStructure] = useState<Structure>();
  const [structureData, setStructureData] = useState<{
    name: string;
    address: string;
    city: string;
    contactEmail: string;
    latitude: number;
    longitude: number;
    province: string;
    cap: string;
    preference?: BookingMode;
  }>();
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadProps["fileList"]>();
  const [structureUrl, setStructureUrl] = useState<string>();

  const initStructure = useCallback(() => {
    setStructure(undefined);
    Structures.getHostStructure().then(
      ({ data }) => {
        setStructure(data.structure);
        setStructureData({
          name: data.structure.name,
          address: data.structure.address,
          city: data.structure.city,
          contactEmail: data.structure.contactEmail || "",
          latitude: data.structure.latitude,
          longitude: data.structure.longitude,
          province: data.structure.province,
          cap: data.structure.cap + "",
          preference: data.structure.preference || BookingMode.Slots,
        });
        Structures.getStructureImageUrl(data.structure.id).then(
          ({ data: imageData }) => {
            setStructureUrl(imageData.url);
            setFileList([
              {
                uid: data.structure.id,
                name: imageData.url.split("/")[4].split("?")[0],
                status: "done",
                url: imageData.url,
              },
            ]);
          },
          () => {
            setStructureUrl(undefined);
            setFileList([]);
          }
        );
        setLoading(false);
      },
      () => {
        setStructure(undefined);
        setLoading(false);
        showToast(
          "structure-retrieve-error",
          "Errore nel reperire i dati della Struttura, per favore riprova.",
          "",
          "danger"
        );
        Cookies.remove(window.__env.AUTH_TOKEN_NAME || "get-move-token");
        Cookies.remove(window.__env.AUTH_USER_NAME || "get-move-user");
        navigate("/");
      }
    );
  }, [showToast, navigate]);

  useEffect(() => {
    initStructure();
  }, [initStructure]);

  const reset = useCallback(() => {
    initStructure();
  }, [initStructure]);

  const onFieldChange = useCallback(
    (key: string) => (evt: React.ChangeEvent<HTMLInputElement>) => {
      const value = evt.target.value;
      setStructureData((previous) => ({
        ...previous!,
        [key]: value,
      }));
    },
    []
  );

  const onPreferenceChange = useCallback((evt: any) => {
    setStructureData((previous) => ({
      ...previous!,
      preference: evt,
    }));
  }, []);

  const onAddressChange = useCallback(
    (value: {
      name: string;
      lat: number;
      lng: number;
      locality: string;
      postalCode: string;
    }) => {
      setStructureData((previous) => ({
        ...previous!,
        address: value.name,
        latitude: value.lat,
        longitude: value.lng,
        city: value.locality,
        cap: value.postalCode,
      }));
    },
    []
  );

  const onCityChange = useCallback(
    (value: {
      name: string;
      lat: number;
      lng: number;
      locality: string;
      postalCode: string;
    }) => {
      setStructureData((previous) => ({
        ...previous!,
        address: "",
        latitude: value.lat,
        longitude: value.lng,
        city: value.locality,
        cap: value.postalCode,
      }));
    },
    []
  );

  const handleChange: UploadProps["onChange"] = ({ file, fileList }) => {
    if (file.status === "removed") {
      setFileList([]);
    } else {
      const newFile: UploadFile = {
        uid: file.uid,
        name: file.name,
        status: "done",
        url: URL.createObjectURL(
          fileList[fileList.length - 1].originFileObj as Blob
        ),
      };

      setFileList([newFile]);
    }
  };

  const blobUrlToFile = async (
    blobUrl: string,
    fileName: string,
    mimeType: string
  ) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new File([blob], fileName, { type: mimeType });
  };

  const handleEditStructure = useCallback(async () => {
    const fd = new FormData();
    Object.entries(structureData!).forEach(([key, value]) => {
      fd.append(key, value.toString());
    });
    if (fileList && fileList[0].url !== structureUrl) {
      const file = await blobUrlToFile(
        fileList[0]!.url!,
        fileList[0].name,
        getMimeType(fileList[0].name)
      );
      fd.append("image", file);
    }
    setEditLoading(true);
    Structures.updateStructure(structure!.id, fd).then(
      () => {
        User.getUserInfo(token!).then(
          ({ data }) => {
            setLoggedUser(data);
            Cookies.set(
              window.__env.AUTH_USER_NAME || "get-move-user",
              JSON.stringify(data),
              { expires: 86400 }
            );
            reset();
            showToast(
              "structure-data-update-success",
              "Dati della Struttura correttamente aggiornati",
              "",
              "success"
            );
            setEditLoading(false);
          },
          (err) => {
            showToast(
              "user-error",
              "Errore nella validazione dell'utente",
              "",
              "danger"
            );
            setEditLoading(false);
          }
        );
      },
      () => {
        setEditLoading(false);
        showToast(
          "structure-update-error",
          "Errore nell'aggiornamento dei dati della Struttura, per favore riprova.",
          "",
          "danger"
        );
      }
    );
  }, [structureData, fileList, reset, showToast, structure, structureUrl, setLoggedUser, token]);

  const handleEditSubmit = useCallback(() => {
    if (editing) {
      handleEditStructure();
    } else {
      setEditing(true);
    }
  }, [editing, handleEditStructure]);

  const handleEditCloseClick = useCallback(() => {
    setStructureData({
      name: structure!.name,
      address: structure!.address,
      city: structure!.city,
      contactEmail: structure!.contactEmail || "",
      latitude: structure!.latitude,
      longitude: structure!.longitude,
      province: structure!.province,
      cap: structure!.cap + "",
      preference: structure!.preference,
    });
    setEditing(false);
  }, [structure]);

  const renderAsyncContent = () => {
    return loading ? (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    ) : (
      <>
        {structure && (
          <Row>
            <Col xs={12} md={6}>
              <Form.Item labelAlign="left" label="Nome Struttura">
                <Input
                  className="w-100"
                  disabled={!editing}
                  placeholder="Nome Struttura..."
                  value={structureData!.name}
                  onChange={onFieldChange("name")}
                />
              </Form.Item>
              <Form.Item labelAlign="left" label="Indirizzo">
                <GooglePlacesSelect
                  placeholder="Seleziona un indirizzo..."
                  value={structureData?.address || ""}
                  disabled={!editing}
                  onSelectCity={onAddressChange}
                />
              </Form.Item>
              <Form.Item labelAlign="left" label="Città">
                <GooglePlacesSelect
                  placeholder="Seleziona una città..."
                  city={true}
                  value={structureData?.city || ""}
                  disabled={!editing}
                  onSelectCity={onCityChange}
                />
              </Form.Item>
              <Form.Item labelAlign="left" label="CAP">
                <Input
                  disabled={!editing}
                  placeholder="CAP..."
                  value={structureData?.cap || ""}
                  onChange={onFieldChange("cap")}
                />
              </Form.Item>
              <Form.Item
                labelAlign="left"
                label="Preferenza Visualizzazione Dashboard"
              >
                <Select
                  disabled={!editing}
                  value={structureData?.preference || BookingMode.Slots}
                  placeholder="Modalità..."
                  options={[
                    {
                      label: "Libera",
                      value: BookingMode.Free,
                    },
                    {
                      label: "Fasce Orarie",
                      value: BookingMode.Slots,
                    },
                  ]}
                  onSelect={onPreferenceChange}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                rules={[
                  {
                    type: "email",
                    message: "Inserisci un indirizzo e-mail valido",
                  },
                ]}
                labelAlign="left"
                label="E-mail di contatto"
              >
                <Input
                  type="email"
                  disabled={!editing}
                  placeholder="E-mail di contatto..."
                  value={structureData?.contactEmail || ""}
                  onChange={onFieldChange("contactEmail")}
                />
              </Form.Item>
              <Form.Item labelAlign="left" label="Logo">
                <Upload
                  disabled={!editing}
                  fileList={fileList}
                  listType="picture"
                  showUploadList={{ showDownloadIcon: true }}
                  beforeUpload={() => false}
                  onChange={handleChange}
                >
                  <Button
                    disabled={!editing}
                    icon={<FontAwesomeIcon icon={faUpload} />}
                  >
                    Carica un altro logo
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        )}
      </>
    );
  };

  const renderToolbar = useCallback(() => {
    return (
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">La tua Struttura</h4>
        </Col>

        <Col xs={6} md={4} className="text-end">
          <button
            disabled={
              editLoading || (editing && (!fileList || fileList.length === 0))
            }
            type="submit"
            className={classNames("button-primary", {
              "button-disabled":
                editLoading ||
                (editing && (!fileList || fileList.length === 0)),
            })}
          >
            {editLoading ? (
              <TailSpin color="#FFF" width={15} height={15} />
            ) : (
              <>
                <FontAwesomeIcon icon={editing ? faSave : faPencil} />
                <span className="d-none d-sm-inline ms-2">
                  {editing ? "Salva Modifiche" : "Modifica Dati"}
                </span>
              </>
            )}
          </button>
          {editing && (
            <button
              disabled={editLoading}
              onClick={handleEditCloseClick}
              className={classNames("button-secondary", "ms-2", {
                "button-disabled":
                  editLoading ||
                  (editing && (!fileList || fileList.length === 0)),
              })}
            >
              {editLoading ? (
                <TailSpin color="#FFF" width={15} height={15} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faCancel} />
                  <span className="d-none d-sm-inline ms-2">Annulla</span>
                </>
              )}
            </button>
          )}
        </Col>
      </Row>
    );
  }, [editLoading, editing, fileList, handleEditCloseClick]);

  return (
    <Container fluid="md" className="mt-5">
      <Form {...formLayout} form={form} onFinish={handleEditSubmit}>
        {renderToolbar()}
        <hr />
        {renderAsyncContent()}
      </Form>
    </Container>
  );
}
